import React from 'react';
import { Button, Form } from 'antd';
import { Colors } from '@common/tokens';
import { createMessage } from 'works/legacy/lib/message-helper';
import { useMutation, useQuery, useNavigate, useParams } from '@common/hooks';
import {
  SupplierInvitationByTokenQuery,
  SupplierInvitationByTokenResponse,
  SupplierInvitationByTokenVariables,
  SupplierInvitationAnswerMutation,
  SupplierInvitationAnswerResponse,
  SupplierInvitationAnswerVariables,
} from '@institutions/graphql';
import {
  Envelope,
  ErrorHandler,
  LoadingState,
  PostMessage,
  TextArea,
} from '@common/components';
import { useSetBreadcrumbs } from 'works/legacy/lib/client-store';

type Params = {
  invitationToken: string;
};

function showSuccessMessage(action: string, organizationName?: string) {
  createMessage(
    'success',
    <>
      You have successfully <strong>{action}</strong> {organizationName}
      &apos;s invitation.
    </>
  );
}

export function OrganizationInvitation() {
  const [form] = Form.useForm();
  const { invitationToken } = useParams<Params>();
  const navigate = useNavigate();
  const goToHomePage = () => navigate('/works/supplier');

  useSetBreadcrumbs([
    {
      name: 'Admin',
      path: '/works/supplier/admin',
    },
    {
      name: 'Organization Invitation',
      path: `/works/supplier/admin/organization-invitation/${invitationToken}`,
    },
  ]);

  const { loading, error, data } = useQuery<
    SupplierInvitationByTokenResponse,
    SupplierInvitationByTokenVariables
  >(SupplierInvitationByTokenQuery, {
    variables: {
      id: invitationToken || '',
    },
  });

  const [answer, { loading: loadingButton }] = useMutation<
    SupplierInvitationAnswerResponse,
    SupplierInvitationAnswerVariables
  >(SupplierInvitationAnswerMutation);

  function handleInvitationExpired() {
    const graphQLErrors = error?.graphQLErrors || [];
    const errorCode = graphQLErrors[0]?.extensions?.code as string;

    return ['INVALID', 'EXPIRED'].includes(errorCode);
  }

  const isInvitationExpired = handleInvitationExpired();

  if (loading) return <LoadingState />;

  if (error && !isInvitationExpired) {
    return <ErrorHandler error={error} log="OrganizationInvitation" />;
  }

  const { organization, createdAt, createdBy, lastComment } =
    data?.supplierInvitationByToken || {};

  async function answerWithAccept(accept: boolean) {
    if (!data) return;

    const answerMessage: string = form.getFieldValue('reply') || '';

    const { errors } = await answer({
      variables: {
        id: data.supplierInvitationByToken.id,
        input: {
          accept,
          answerMessage,
        },
      },
    });
    if (errors) {
      createMessage('error', errors.map(({ message }) => message).join(' '));

      return;
    }
    showSuccessMessage(accept ? 'accepted' : 'declined', organization?.name);
    goToHomePage();
  }

  const handleAccept = () => answerWithAccept(true);
  const handleDecline = () => answerWithAccept(false);

  return (
    <>
      <div className="organization-invitation-page">
        {isInvitationExpired ? (
          <Envelope
            title="This invitation link is expired"
            text="This link is expired, or another member from your organization has responded to this request."
            illustration="broken-link"
            footerRight={<Button onClick={goToHomePage}>OK</Button>}
          />
        ) : (
          <Envelope
            title={`${organization?.name} has invited you to become a Supplier`}
            text={`By accepting, ${organization?.name}'s admins will be notified. You will receive
              another email notification when you've been activated as a
              Supplier and can start viewing their Staffing Requests.`}
            illustration="mail-letters"
            footerLeft={
              <Button type="link" onClick={goToHomePage}>
                Back
              </Button>
            }
            footerRight={
              <>
                <Button danger onClick={handleDecline} loading={loadingButton}>
                  Decline
                </Button>
                <Button
                  type="primary"
                  onClick={handleAccept}
                  loading={loadingButton}
                >
                  Accept
                </Button>
              </>
            }
          >
            <Form layout="vertical" form={form}>
              {lastComment && (
                <PostMessage
                  className="organization-invitation-message"
                  message={lastComment?.contentAsText || ''}
                  name={createdBy?.name || ''}
                  time={createdAt || ''}
                />
              )}
              <Form.Item label="Reply" name="reply">
                <TextArea
                  maxLength={500}
                  placeholder={`Add a note for ${organization?.name}`}
                />
              </Form.Item>
            </Form>
          </Envelope>
        )}
      </div>
      <style jsx>{`
        .organization-invitation-page {
          background-color: ${Colors.elmHyperlight};
          height: 100%;
        }

        .organization-invitation-page :global(.wk-envelope) {
          margin: 58px auto 0 auto;
        }

        :global(.organization-invitation-message.wk-post-message) {
          margin-bottom: 24px;
        }
      `}</style>
    </>
  );
}
